import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useRealtimeSubscription } from '../lib/realtime';
import type { ScheduleEntry, ScheduleViewMode } from '../types';
import type { ScheduleUser } from '../components/schedule/scheduleHelpers';

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function getDateRange(
  anchorDate: Date,
  viewMode: ScheduleViewMode,
): { rangeStart: string; rangeEnd: string } {
  if (viewMode === 'day') {
    const str = formatDate(anchorDate);
    return { rangeStart: `${str}T00:00:00`, rangeEnd: `${str}T23:59:59` };
  }
  if (viewMode === 'week') {
    const end = new Date(anchorDate);
    end.setDate(end.getDate() + 6);
    return {
      rangeStart: `${formatDate(anchorDate)}T00:00:00`,
      rangeEnd: `${formatDate(end)}T23:59:59`,
    };
  }
  // month — extend to full calendar grid (Mon–Sun)
  const year = anchorDate.getFullYear();
  const month = anchorDate.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);
  const dow = firstOfMonth.getDay();
  const startOffset = dow === 0 ? 6 : dow - 1;
  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(gridStart.getDate() - startOffset);
  const endDow = lastOfMonth.getDay();
  const endOffset = endDow === 0 ? 0 : 7 - endDow;
  const gridEnd = new Date(lastOfMonth);
  gridEnd.setDate(gridEnd.getDate() + endOffset);
  return {
    rangeStart: `${formatDate(gridStart)}T00:00:00`,
    rangeEnd: `${formatDate(gridEnd)}T23:59:59`,
  };
}

interface UseScheduleEntriesReturn {
  entries: ScheduleEntry[];
  users: ScheduleUser[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useScheduleEntries(
  businessId: string | undefined,
  anchorDate: Date,
  viewMode: ScheduleViewMode,
): UseScheduleEntriesReturn {
  const [entries, setEntries] = useState<ScheduleEntry[]>([]);
  const [users, setUsers] = useState<ScheduleUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const { rangeStart, rangeEnd } = useMemo(
    () => getDateRange(anchorDate, viewMode),
    [anchorDate, viewMode],
  );

  const fetchAll = useCallback(async () => {
    if (!businessId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);

    try {
      const [entriesResult, usersResult] = await Promise.all([
        supabase
          .from('schedule_entries')
          .select('*')
          .eq('business_id', businessId)
          .gte('start_at', rangeStart)
          .lte('start_at', rangeEnd)
          .order('start_at')
          .abortSignal(controller.signal),
        supabase
          .from('users')
          .select('id, first_name, last_name, role, employment_type')
          .eq('business_id', businessId)
          .eq('status', 'active')
          .abortSignal(controller.signal),
      ]);
      if (mountedRef.current) {
        setEntries((entriesResult.data ?? []) as ScheduleEntry[]);
        setUsers((usersResult.data ?? []) as ScheduleUser[]);
      }
    } catch (err) {
      if (mountedRef.current) {
        const msg = err instanceof Error ? err.message : 'Failed to load schedule';
        setError(msg.includes('aborted') ? 'Request timed out' : msg);
      }
    } finally {
      clearTimeout(timeout);
      if (mountedRef.current) setLoading(false);
    }
  }, [businessId, rangeStart, rangeEnd]);

  useEffect(() => {
    mountedRef.current = true;
    void fetchAll();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchAll]);

  const rtTables = useMemo(() => ['schedule_entries'], []);
  useRealtimeSubscription(businessId, rtTables, fetchAll);

  return {
    entries, users, loading, error, refresh: fetchAll,
  };
}
